import React, {
  useEffect, useRef, useCallback, useMemo,
} from 'react';
import cn from 'classnames';
import _uniqBy from 'lodash/uniqBy';
import _isEqual from 'lodash/isEqual';
import { useSelector } from 'react-redux';
import { Button, Input, DatePicker } from '@picsio/ui';
import { MIME_TYPES } from '@picsio/db/src/constants';
import {
  Colors, Flags, StarRating, ReactSelect,
} from '../../UIComponents';
import sendEventToIntercom from '../../services/IntercomEventService';
import { SINGLE_OPERATORS, MULTIPLE_OPERATORS, STRING_TYPE_OPERATORS, ARRAY_TYPE_OPERATORS } from './config';
import l10n from '../../shared/strings';
import Keywords from '../Search/Keywords';
import Logger from '../../services/Logger';
import DropdownTree, { DropdownTreeWithStore } from '../DropdownTree';
import Faces from '../Search/Faces';
import Dropdown from '../dropdown';
import AssigneesDropdown from '../assigneesDropdown';
import InputDateRange from '../../UIComponents/inputDateRange';
import Tooltip from '../Tooltip/Tooltip';

export default function SearchFilterDropOpener({
  id,
  isDropdownOpened,
  operators,
  options,
  type,
  subtype,
  selectedFilterData = {},
  confirmedData = {},
  onSelectFilter,
  onConfirmFilter,
  onResetFilter,
  onCloseDropdown,
  isSelected = false,
  btnRef,
  filters,
  title,
}) {
  const treeListItems = useSelector((state) => state.collections.collections?.my);
  const lightboards = useSelector((state) => state.lightboards.lightboards);
  const inboxes = useSelector((state) => state.inboxes.inboxes);
  const watermarks = useSelector((state) => state.assets.watermarks);
  const customFields = useSelector((state) => state.customFields.items);
  const dropRef = useRef();

  const handleConfirm = useCallback(() => {
    onConfirmFilter(id, selectedFilterData);
    sendEventToIntercom('AdvFilterPanelUsed');
  }, [selectedFilterData, id, onConfirmFilter]);

  const isSingleOperator = useMemo(() => SINGLE_OPERATORS.includes(selectedFilterData.operator), [selectedFilterData.operator]);

  const isMultipleOperator = useMemo(() => MULTIPLE_OPERATORS.includes(selectedFilterData.operator), [selectedFilterData.operator]);

  const isConfirmButtonHidden = useCallback((operator, filterType) => {
    if (options && options.length && !SINGLE_OPERATORS.includes(operator)) return true;
    if (MULTIPLE_OPERATORS.includes(operator)) return true;
    const filtersWithButton = ['string', 'text', 'number', 'mimeType'];
    if (filtersWithButton.includes(filterType) && !SINGLE_OPERATORS.includes(operator)) {
      if (filterType === 'mimeType' && operator === 'oneOf') {
        return true;
      }
      return false;
    } return true;
  }, [options]);

  const isDisabled = useMemo(() => {
    let isFilterValueEmpty;
    if (Array.isArray(selectedFilterData.value)) {
      if (selectedFilterData.operator === 'between') {
        isFilterValueEmpty = selectedFilterData.value.length < 2;
      } else {
        isFilterValueEmpty = !selectedFilterData.value.length;
      }
    } else {
      isFilterValueEmpty = !selectedFilterData.value;
    }

    const inCorrectRange = Array.isArray(selectedFilterData.value) && selectedFilterData.value.length === 2 && (type === 'number' || type === 'rating') && Number(selectedFilterData.value[1]) < Number(selectedFilterData.value[0]);
    const isFilterUnchanged = _isEqual(selectedFilterData, confirmedData) && isSelected && !isSingleOperator;

    if (isMultipleOperator) {
      const isFilterExcludedValueEmpty = Array.isArray(selectedFilterData?.excludedValues) ? !selectedFilterData?.excludedValues?.length : !selectedFilterData?.excludedValues;
      return (isFilterValueEmpty || isFilterExcludedValueEmpty);
    }

    return (!isSingleOperator && isFilterValueEmpty) || isFilterUnchanged || inCorrectRange;
  }, [selectedFilterData, type, confirmedData, isSelected, isSingleOperator, isMultipleOperator]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropRef.current
        && !btnRef.current.contains(event.target)
        && !dropRef.current.contains(event.target)
        && !event.target.closest('.react-select__indicator')
        && !event.target.closest('.dropdown-item')
      ) {
        onCloseDropdown(event);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Enter' && !isDisabled) {
        handleConfirm();
      }
    };

    if (isDropdownOpened) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpened, onCloseDropdown, handleConfirm, btnRef, isDisabled]);

  const handleResetFilter = useCallback(() => {
    onResetFilter(id);
  }, [id, onResetFilter]);

  const handleSelectFilter = useCallback((filterId, filterData, isEmpty) => {
    // when user selected filter operator and filter value
    if (isConfirmButtonHidden(filterData.operator, type) && !isEmpty) {
      if (filterData.value || (MULTIPLE_OPERATORS.includes(filterData.operator) && filterData.excludedValues?.length > 0)) {
        onConfirmFilter(filterId, filterData, (SINGLE_OPERATORS.includes(filterData.operator) || (options?.length > 0)));
      }
      onSelectFilter(filterId, filterData);
    } else if (isConfirmButtonHidden(filterData.operator, type) && isEmpty && MULTIPLE_OPERATORS) { // when user deselects
      handleResetFilter();
    } else {
      onSelectFilter(filterId, filterData);
    }
  }, [handleResetFilter, isConfirmButtonHidden, onConfirmFilter, onSelectFilter, options?.length, type]);

  const createFilterData = useCallback((operatorValue, value, excludedValues) => {
    if (MULTIPLE_OPERATORS.includes(operatorValue)) {
      return ({ operator: operatorValue, value, excludedValues });
    }
    return ({ operator: operatorValue, value });
  }, []);

  const handleChangeOption = useCallback((data) => {
    handleSelectFilter(id, createFilterData(selectedFilterData.operator || operators[0], data.value), false);
  }, [createFilterData, selectedFilterData.operator, operators, handleSelectFilter, id]);

  const selectKeyword = (items) => {
    let selectedKeywords = selectedFilterData.value || [];
    const operator = selectedFilterData.operator || operators[0];
    if (operator === 'is' || operator === 'isNot') {
      selectedKeywords = [items];
    } else if (items.length) {
      selectedKeywords = [...selectedKeywords, ...items];
    } else {
      selectedKeywords = [...selectedKeywords, items];
    }
    selectedKeywords = _uniqBy(selectedKeywords, '_id');
    const createdFilterData = createFilterData(operator, selectedKeywords, selectedFilterData.excludedValues);
    const isSearchEmpty = false;
    handleSelectFilter(id, createdFilterData, isSearchEmpty);
    Logger.log('User', 'SearchAdvancedSelectKeywords', {
      value: selectedKeywords.map((kw) => kw.title),
      excludedValues: selectedFilterData.excludedValues?.map((kw) => kw.title) || [],
    });
  };

  const deselectKeyword = (item) => {
    const selectedKeywords = [...(selectedFilterData.value || [])];
    const itemIndex = selectedKeywords.findIndex((keyword) => keyword._id === item._id);
    const operator = selectedFilterData.operator || operators[0];
    const excludedValues = selectedFilterData.excludedValues || [];
    if (itemIndex !== -1) {
      selectedKeywords.splice(itemIndex, 1);
      const isEmpty = selectedKeywords.length === 0 && excludedValues.length === 0;
      handleSelectFilter(id, createFilterData(operator, selectedKeywords, excludedValues), isEmpty);
      Logger.log('User', 'SearchAdvancedDeselectKeywords', {
        value: selectedKeywords.map((kw) => kw.title),
        excludedValues: excludedValues.map((kw) => kw.title) || [],
      });
    }
  };

  const selectFace = (items) => {
    const operator = selectedFilterData.operator || operators[0];
    let selectedFaces = selectedFilterData.value || [];
    if (items.length) {
      selectedFaces = [...selectedFaces, ...items];
    } else {
      selectedFaces = [...selectedFaces, items];
    }
    selectedFaces = _uniqBy(selectedFaces, '_id');

    handleSelectFilter(id, createFilterData(operator, selectedFaces, selectedFilterData.excludedValues), false);
    Logger.log('User', 'SearchAdvancedSelectFaces', {
      value: selectedFaces.map((face) => face.name),
      excludedValues: selectedFilterData.excludedValues?.map((face) => face.name) || [],
    });
  };

  const deselectFace = (item) => {
    const operator = selectedFilterData.operator || operators[0];
    const selectedFaces = [...(selectedFilterData.value || [])];
    const itemIndex = selectedFaces.findIndex((face) => face._id === item._id);
    const excludedValues = selectedFilterData.excludedValues || [];
    if (itemIndex !== -1) {
      selectedFaces.splice(itemIndex, 1);
      const isEmpty = selectedFaces.length === 0 && excludedValues.length === 0;
      handleSelectFilter(id, createFilterData(operator, selectedFaces, excludedValues), isEmpty);
      Logger.log('User', 'SearchAdvancedDeselectKeywords', {
        value: selectedFaces?.map((face) => face.name),
        excludedValues: excludedValues?.map((face) => face.name) || [],
      });
    }
  };

  const selectFaceParams2 = (items) => {
    const operator = selectedFilterData.operator || operators[0];
    let selectedExcludedFaces = selectedFilterData.excludedValues || [];
    if (items.length) {
      selectedExcludedFaces = [...selectedExcludedFaces, ...items];
    } else {
      selectedExcludedFaces = [...selectedExcludedFaces, items];
    }
    selectedExcludedFaces = _uniqBy(selectedExcludedFaces, '_id');

    handleSelectFilter(id, createFilterData(operator, selectedFilterData.value, selectedExcludedFaces), false);
    Logger.log('User', 'SearchAdvancedExcludedSelectFaces', {
      excludedValues: selectedExcludedFaces?.map((face) => face.name),
      value: selectedFilterData.value?.map((face) => face.name),
    });
  };

  const deselectFaceParams2 = (item) => {
    const operator = selectedFilterData.operator || operators[0];
    const selectedExcludedFaces = [...(selectedFilterData.excludedValues || [])];
    const itemIndex = selectedExcludedFaces.findIndex((face) => face._id === item._id);

    if (itemIndex !== -1) {
      selectedExcludedFaces.splice(itemIndex, 1);
      const isEmpty = selectedExcludedFaces.length === 0 && (selectedFilterData.value || []).length === 0;
      handleSelectFilter(id, createFilterData(operator, selectedFilterData.value, selectedExcludedFaces), isEmpty);
      Logger.log('User', 'SearchAdvancedExcludedDeselectKeywords', {
        value: selectedFilterData.value?.map((face) => face.name),
        excludedValues: selectedExcludedFaces?.map((face) => face.name),
      });
    }
  };

  const customFieldsOptions = useMemo(() => {
    if (id.startsWith('meta.') && type === 'enum') {
      const selectedCustomField = customFields.find((customField) => customField.title === id.split('meta.')[1]);
      selectedCustomField.items = selectedCustomField.options.map((option) => ({ title: option, value: option, _id: option }));
      return selectedCustomField;
    }
    return null;
  }, [id, type, customFields]);

  const selectDropdown = (item) => {
    const operator = selectedFilterData.operator || operators[0];
    let selectedItems = selectedFilterData.value || [];

    if (operator === 'equals') {
      selectedItems = [item];
    } else {
      selectedItems = [...selectedItems, item];
    }
    handleSelectFilter(id, createFilterData(operator, selectedItems, [...(selectedFilterData.excludedValues || [])]), false);
  };

  const deselectDropdown = (item) => {
    const operator = selectedFilterData.operator || operators[0];
    const selectedItems = (selectedFilterData.value || []).filter(({ _id }) => _id !== item._id);
    const excludedValues = selectedFilterData.excludedValues || [];
    const isEmpty = selectedItems.length === 0 && excludedValues.length === 0;
    handleSelectFilter(id, createFilterData(operator, selectedItems, excludedValues), isEmpty);
  };

  const selectDropdownSecondParams = useCallback((itemToExclude) => {
    const operator = selectedFilterData.operator || operators[0];
    const excludedValues = [...(selectedFilterData.excludedValues || []), itemToExclude];

    handleSelectFilter(id, createFilterData(operator, [...(selectedFilterData.value || [])], excludedValues), false);
  }, [handleSelectFilter, createFilterData, selectedFilterData, id, operators]);

  const deselectDropdownSecondParams = useCallback((itemToExclude) => {
    const operator = selectedFilterData.operator || operators[0];
    const excludedValues = (selectedFilterData.excludedValues || []).filter(({ _id }) => _id !== itemToExclude._id);
    const isEmpty = excludedValues.length === 0 && (selectedFilterData.value || []).length === 0;
    handleSelectFilter(id, createFilterData(operator, [...(selectedFilterData.value || [])], excludedValues), isEmpty);
  }, [selectedFilterData.operator, selectedFilterData.excludedValues, selectedFilterData.value, operators, handleSelectFilter, id, createFilterData]);

  const onChangeFlags = (value) => {
    const operator = selectedFilterData.operator || operators[0];
    let flags = [...(selectedFilterData.value || [])];
    const flagIndex = flags.indexOf(value);
    if (flagIndex > -1) {
      flags.splice(flagIndex, 1);
    } else if (operator === 'equals') {
      flags = [value];
    } else {
      flags.push(value);
    }

    handleSelectFilter(id, createFilterData(operator, flags), flags.length === 0);
    Logger.log('User', 'SearchAdvancedFlags', { value: flags });
  };

  const deselectFlags = () => {
    onResetFilter(id);
  };

  const onChangeColors = (value) => {
    const operator = selectedFilterData.operator || operators[0];
    let colors = [...(selectedFilterData.value || [])];
    const colorIndex = colors.indexOf(value);
    if (colorIndex > -1) {
      colors.splice(colorIndex, 1);
    } else if (operator === 'equals') {
      colors = [value];
    } else {
      colors.push(value);
    }

    handleSelectFilter(id, createFilterData(operator, colors), colors.length === 0);
    Logger.log('User', 'SearchAdvancedColors', { value: colors });
  };

  const deselectColors = (e) => {
    e.stopPropagation();
    onResetFilter(id);
  };

  const onChangeRating = (value) => {
    const operator = selectedFilterData.operator || operators[0];
    const rating = selectedFilterData.value === value ? 0 : value;
    handleSelectFilter(id, createFilterData(operator, rating), rating === 0);
    Logger.log('User', 'SearchAdvancedRating', { value: rating });
  };

  const deselectStars = (e) => {
    e.stopPropagation();
    onResetFilter(id);
  };

  const onChangeDate = (date) => {
    let newDate = date;
    if (typeof date !== 'string') {
      newDate = new Date(date).getTime();
    }
    const operator = selectedFilterData.operator || operators[0];
    handleSelectFilter(id, createFilterData(operator, newDate), false);
    Logger.log('User', 'SearchAdvancedDate', { value: newDate });
  };

  const handleSelectCollection = useCallback((collectionItem, isChecked) => {
    const operator = selectedFilterData.operator || operators[0];
    if (isChecked) {
      if (selectedFilterData.value?.length > 1) {
        const items = selectedFilterData.value.filter((checkedItem) => checkedItem._id !== collectionItem._id);
        handleSelectFilter(id, createFilterData(operator, items), false);
      }
      return;
    }
    handleSelectFilter(id, createFilterData(operator, (selectedFilterData.value || []).concat(collectionItem)), false);
  }, [createFilterData, selectedFilterData.operator, selectedFilterData.value, id, handleSelectFilter, operators]);

  const handleSelectLightboard = useCallback((collectionItem) => {
    const operator = selectedFilterData.operator || operators[0];
    handleSelectFilter(id, createFilterData(operator, [collectionItem]), false);
  }, [createFilterData, selectedFilterData.operator, id, handleSelectFilter, operators]);

  const handleSelectInbox = useCallback((collectionItem) => {
    const operator = selectedFilterData.operator || operators[0];
    handleSelectFilter(id, createFilterData(operator, [collectionItem]), false);
  }, [createFilterData, selectedFilterData.operator, id, handleSelectFilter, operators]);

  const handleChangeSearchInput = useCallback((event) => {
    const operator = selectedFilterData.operator || operators[0];
    const raw = event.target.value;
    if (raw.length > 0 && raw.trim().length === 0) {
      return;
    }
    handleSelectFilter(id, createFilterData(operator, raw));
  }, [createFilterData, selectedFilterData.operator, id, handleSelectFilter, operators]);

  const handleDoubleInput = useCallback((index) => (event) => {
    const operator = selectedFilterData.operator || operators[0];
    const value = event.target.value.trim();
    const values = (Array.isArray(selectedFilterData.value) ? [...selectedFilterData.value] : selectedFilterData.value || []);
    values[index] = Number(value);
    handleSelectFilter(id, createFilterData(operator, values));
  }, [createFilterData, selectedFilterData.operator, selectedFilterData.value, id, handleSelectFilter, operators]);

  const onChangeDoubleRating = useCallback((index) => (value) => {
    const operator = selectedFilterData.operator || operators[0];
    const values = (Array.isArray(selectedFilterData.value) ? [...selectedFilterData.value] : selectedFilterData.value || []);
    values[index] = Number(value);
    handleSelectFilter(id, createFilterData(operator, values));
  }, [createFilterData, selectedFilterData.operator, selectedFilterData.value, id, handleSelectFilter, operators]);

  const watermarksOptions = useMemo(() => watermarks.map(
    (watermark) => ({
      ...watermark, title: watermark.name, value: watermark._id,
    }),
  ), [watermarks]);

  const mimeTypeOptions = useMemo(() => {
    const mimeTypes = Object.keys(MIME_TYPES).reduce((acc, key) => [...acc, ...MIME_TYPES[key]], []);
    return mimeTypes.map((item) => ({
      title: item, value: item, _id: item,
    }));
  }, []);

  const checkForOperatorCompatibility = useCallback((newOperator) => {
    if (STRING_TYPE_OPERATORS.includes(newOperator) && STRING_TYPE_OPERATORS.includes(selectedFilterData.operator)) {
      return true;
    }
    if (ARRAY_TYPE_OPERATORS.includes(newOperator) && ARRAY_TYPE_OPERATORS.includes(selectedFilterData.operator)) {
      return true;
    } return false;
  }, [selectedFilterData]);

  const handleSelectOperator = useCallback((data) => {
    if (SINGLE_OPERATORS.includes(data.value)) {
      handleSelectFilter(id, createFilterData(data.value, true), false);
    } else if (isSelected
      && !SINGLE_OPERATORS.includes(confirmedData.operator)
      && confirmedData.operator !== 'between'
      && data.value !== 'between'
      && checkForOperatorCompatibility(data.value)) {
      handleSelectFilter(id, createFilterData(data.value, confirmedData.value, confirmedData.excludedValues), false);
    } else if (!isSelected && !SINGLE_OPERATORS.includes(selectedFilterData.operator) && selectedFilterData.operator !== 'between' && data.value !== 'between') {
      handleSelectFilter(id, createFilterData(data.value, selectedFilterData.value), false);
    } else {
      handleSelectFilter(id, createFilterData(data.value, null), false);
    }
  }, [isSelected, confirmedData.operator, confirmedData.value, checkForOperatorCompatibility, selectedFilterData.operator, selectedFilterData.value, handleSelectFilter, id, createFilterData]);

  const getOptionsLabel = useMemo(() => {
    const selectedOption = options?.find((option) => option.value === selectedFilterData.value);
    if (selectedOption) {
      return { label: selectedOption.label, value: selectedOption.value };
    } return null;
  }, [selectedFilterData.value, options]);

  const operatorValue = useMemo(() => {
    if (selectedFilterData.operator) {
      if (id.startsWith('meta.')) {
        if (type === 'number') {
          return { label: filters.meta.operatorNamesNumber[selectedFilterData.operator], value: selectedFilterData.operator };
        }
        if (type === 'date') {
          return { label: filters.meta.operatorNamesDate[selectedFilterData.operator], value: selectedFilterData.operator };
        }
        return { label: filters.meta.operatorNamesString[selectedFilterData.operator], value: selectedFilterData.operator };
      }
      return { label: filters[id]?.operatorNames ? filters[id].operatorNames[selectedFilterData.operator] : selectedFilterData.operator, value: selectedFilterData.operator };
    } return null;
  }, [selectedFilterData.operator, id, type, filters]);

  const operatorLabel = useCallback((item) => {
    if (id.startsWith('meta.')) {
      if (type === 'number') {
        return filters.meta.operatorNamesNumber[item];
      }
      if (type === 'date') {
        return filters.meta.operatorNamesDate[item];
      }
      return filters.meta.operatorNamesString[item];
    }
    return filters[id]?.operatorNames ? filters[id].operatorNames[item] : item;
  }, [id, type, filters]);

  return (
    <div
      className={cn('searchFilterDrop', {})}
      id={id}
      data-testid={id}
      ref={dropRef}
    >
      <If condition={operators && operators.length > 1}>
        <div className="searchFilterDrop__operators">
          <ReactSelect
            options={operators.map((item) => ({ label: operatorLabel(item), value: item }))}
            value={operatorValue}
            onChange={handleSelectOperator}
            placeholder="Select operator"
          />
        </div>
      </If>
      <If condition={options && options.length && !isSingleOperator}>
        <div className="searchFilterDrop__options">
          <ReactSelect
            options={options}
            value={getOptionsLabel}
            onChange={handleChangeOption}
            placeholder={title.toLowerCase().includes('status') ? 'Select status' : 'Select value'}
            useCustomIndicator={false}
          />
        </div>
      </If>
      <div className="searchFilterDrop__content">
        <If condition={(!isSingleOperator && (selectedFilterData.operator || operators.length === 1))}>
          <If condition={isMultipleOperator}>
            <div className="multipleOperator">
              {operatorValue.label.split('...')[0]}
            </div>
          </If>
          <If condition={type === 'keywords'}>
            <Keywords
              selectedKeywords={selectedFilterData.value || []}
              addKeyword={selectKeyword}
              removeKeyword={deselectKeyword}
              changed={selectedFilterData.value?.length > 0}
              withoutLabel
              position="right"
            />
          </If>
          <If condition={type === 'collectionIds'}>
            <DropdownTreeWithStore
              checkedItems={(selectedFilterData.value || []).map((item) => item._id)}
              openedItems={[]}
              treeListItems={treeListItems || []}
              onClick={handleSelectCollection}
              iconSpecial="folder"
              canCreateCollection={false}
              multipleSelection
            />
          </If>
          <If condition={type === 'lightboard'}>
            <DropdownTree
              checkedItems={(selectedFilterData.value || []).map((item) => item._id)}
              openedItems={[]}
              treeListItems={lightboards || []}
              onClick={handleSelectLightboard}
              iconSpecial="folder"
              canCreateCollection={false}
              multipleSelection
            />
          </If>
          <If condition={type === 'inbox'}>
            <DropdownTree
              checkedItems={(selectedFilterData.value || []).map((item) => item._id)}
              openedItems={[]}
              treeListItems={(inboxes || []).map((inbox) => ({ ...inbox, path: '' }))}
              onClick={handleSelectInbox}
              iconSpecial="folder"
              canCreateCollection={false}
              multipleSelection
            />
          </If>
          <If condition={type === 'faces'}>
            <Faces
              selectedFaces={selectedFilterData.value || []}
              addFace={selectFace}
              removeFace={deselectFace}
              changed={(selectedFilterData.value || []).length > 0}
              position="right"
              withoutLabel
            />
          </If>
          <If condition={type === 'flag'}>
            <div className="searchFilterDrop__field">
              <Flags
                value={selectedFilterData.value || []}
                onChange={onChangeFlags}
                deselectFlags={deselectFlags}
              />
            </div>
          </If>
          <If condition={type === 'color'}>
            <div className="searchFilterDrop__field">
              <Colors
                value={selectedFilterData.value || []}
                onChange={onChangeColors}
                deselectColors={deselectColors}
              />
            </div>
          </If>
          <If condition={type === 'mimeType'}>
            <Choose>
              <When condition={selectedFilterData.operator === 'oneOf'}>
                <Dropdown
                  position="right"
                  items={mimeTypeOptions}
                  filterText="Select mimetypes"
                  onCheckedHandler={selectDropdown}
                  onUncheckedHandler={deselectDropdown}
                  createHandler={null}
                  checkedItems={selectedFilterData.value || []}
                />
              </When>
              <Otherwise>
                <div className="searchFilterDrop__string">
                  <Input
                    placeholder="Value"
                    value={selectedFilterData.value || ''}
                    onChange={handleChangeSearchInput}
                  />
                </div>
              </Otherwise>
            </Choose>
          </If>
          <If condition={type === 'rating'}>
            <Choose>
              <When condition={selectedFilterData.operator === 'between'}>
                <div className="searchFilterDrop__field">
                  <StarRating
                    value={Number((selectedFilterData.value || [])[0])}
                    onChange={onChangeDoubleRating(0)}
                    deselectStars={deselectStars}
                  />
                </div>
                <div className="searchFilterDrop__field">
                  <StarRating
                    value={Number((selectedFilterData.value || [])[1])}
                    onChange={onChangeDoubleRating(1)}
                    deselectStars={deselectStars}
                  />
                </div>
              </When>
              <Otherwise>
                <div className="searchFilterDrop__field">
                  <StarRating
                    value={(Number(Array.isArray(selectedFilterData.value) ? selectedFilterData.value[0] : selectedFilterData.value)) || 0}
                    onChange={onChangeRating}
                    deselectStars={deselectStars}
                  />
                </div>
              </Otherwise>
            </Choose>
          </If>
          <If condition={type === 'assignees' || type === 'user'}>
            <div className="searchFilterDrop__field">
              <AssigneesDropdown
                placeholder={l10n.SEARCH.userSearchFilterPlaceholder}
                placeholderIcon="emptyAvatar"
                icon="avatar"
                filterText={l10n.SEARCH.userSearchFilterLabel}
                checkedItems={selectedFilterData.value || []}
                onCheckedHandler={selectDropdown}
                onUncheckedHandler={deselectDropdown}
                createHandler={null}
                isAllowClickItem
                position="right"
              />
            </div>
          </If>
          <If condition={type === 'watermark'}>
            <div className="searchFilterDrop__field">
              <Dropdown
                position="right"
                items={watermarksOptions}
                filterText="Select watermarks"
                onCheckedHandler={selectDropdown}
                onUncheckedHandler={deselectDropdown}
                createHandler={null}
                checkedItems={selectedFilterData.value || []}
              />
            </div>
          </If>
          <If condition={type === 'date'}>
            <div className="searchFilterDrop__field">
              <Choose>
                <When condition={selectedFilterData.operator === 'between'}>
                  <InputDateRange dontShowSelector value={selectedFilterData.value || 'any'} onChange={onChangeDate} />
                </When>
                <Otherwise>
                  <DatePicker
                    selected={selectedFilterData.value ? new Date(Number(selectedFilterData.value)) : null}
                    datePickerPlaceholder="Select date"
                    onChange={onChangeDate}
                  />
                </Otherwise>
              </Choose>
            </div>
          </If>
          <If condition={type === 'number'}>
            <div className="searchFilterDrop__field">
              <Choose>
                <When condition={selectedFilterData.operator === 'between'}>
                  <div className="searchFilterDrop__doubleInput">
                    <Input
                      label="From"
                      placeholder={id === 'fileSize' ? 'From (bytes)' : 'From'}
                      value={(selectedFilterData.value || [])[0]}
                      onChange={handleDoubleInput(0)}
                      type="number"
                    />
                    <Input
                      label="To"
                      placeholder={id === 'fileSize' ? 'To (bytes)' : 'To'}
                      value={(selectedFilterData.value || [])[1]}
                      onChange={handleDoubleInput(1)}
                      type="number"
                    />
                  </div>
                </When>
                <Otherwise>
                  <Input
                    placeholder={id === 'fileSize' ? 'Value (bytes)' : 'Value'}
                    value={selectedFilterData.value || ''}
                    onChange={handleChangeSearchInput}
                    type="number"
                  />
                </Otherwise>
              </Choose>
            </div>
          </If>
          <If condition={type === 'string' || (type === 'text' && id.startsWith('meta.'))}>
            <div className="searchFilterDrop__string">
              <Choose>
                <When condition={subtype === 'text'}>
                  <Input
                    component="textarea"
                    multiline
                    autoResize
                    minHeight={68}
                    maxHeight={148}
                    value={selectedFilterData.value || ''}
                    onChange={handleChangeSearchInput}
                    placeholder="Value"
                  />
                </When>
                <Otherwise>
                  <Input
                    placeholder="Value"
                    value={selectedFilterData.value || ''}
                    onChange={handleChangeSearchInput}
                  />
                </Otherwise>
              </Choose>
            </div>
          </If>
          <If condition={type === 'enum' && id.startsWith('meta.')}>
            <div className="customFieldsSearch">
              <Dropdown
                position="right"
                items={customFieldsOptions.items}
                filterText="Select custom field options"
                onCheckedHandler={selectDropdown}
                onUncheckedHandler={deselectDropdown}
                createHandler={null}
                checkedItems={selectedFilterData.value || []}
              />
            </div>
          </If>
        </If>
        <If condition={isMultipleOperator}>
          <div className="multipleOperator">
            {operatorValue.label.split('...')[1]}
          </div>
          <If condition={type === 'assignees' || type === 'user'}>
            <div className="searchFilterDrop__field">
              <AssigneesDropdown
                placeholder={l10n.SEARCH.userSearchFilterPlaceholder}
                placeholderIcon="emptyAvatar"
                icon="avatar"
                filterText={l10n.SEARCH.userSearchFilterLabel}
                checkedItems={selectedFilterData.excludedValues || []}
                onCheckedHandler={selectDropdownSecondParams}
                onUncheckedHandler={deselectDropdownSecondParams}
                createHandler={null}
                isAllowClickItem
                position="right"
              />
            </div>
          </If>
          <If condition={type === 'faces'}>
            <Faces
              selectedFaces={selectedFilterData.excludedValues || []}
              addFace={selectFaceParams2}
              removeFace={deselectFaceParams2}
              changed={(selectedFilterData.excludedValues || []).length > 0}
              position="right"
              withoutLabel
            />
          </If>
          <If condition={type === 'keywords'}>
            <Keywords
              selectedKeywords={selectedFilterData.excludedValues || []}
              addKeyword={selectDropdownSecondParams}
              removeKeyword={deselectDropdownSecondParams}
              changed={selectedFilterData.excludedValues?.length > 0}
              withoutLabel
              position="right"
            />
          </If>
          <If condition={type === 'enum' && id.startsWith('meta.')}>
            <div className="customFieldsSearch">
              <Dropdown
                position="right"
                items={customFieldsOptions.items}
                filterText="Select custom field options"
                onCheckedHandler={selectDropdownSecondParams}
                onUncheckedHandler={deselectDropdownSecondParams}
                createHandler={null}
                checkedItems={selectedFilterData.excludedValues || []}
              />
            </div>
          </If>
          <If condition={type === 'watermark'}>
            <div className="searchFilterDrop__field">
              <Dropdown
                position="right"
                items={watermarksOptions}
                filterText="Select watermarks"
                onCheckedHandler={selectDropdownSecondParams}
                onUncheckedHandler={deselectDropdownSecondParams}
                createHandler={null}
                checkedItems={selectedFilterData.excludedValues || []}
              />
            </div>
          </If>
        </If>
      </div>
      <If condition={!isConfirmButtonHidden(selectedFilterData.operator, type)}>
        <div className="searchFilterDrop__controls">
          <Tooltip placement="top" content={isDisabled ? l10n.SEARCH.disabled : ''}>
            <Button
              onClick={handleConfirm}
              variant="contained"
              color="primary"
              size="md"
              disabled={isDisabled}
            >
              {l10n.SEARCH.filterConfirmBtn}
            </Button>
          </Tooltip>
        </div>
      </If>
    </div>
  );
}
